import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PromoBanner from './PromoBanner';

const servicesMenu = [
  { label: 'Heating', path: '/heating', icon: 'ri-fire-line' },
  { label: 'Air Conditioning', path: '/air-conditioning', icon: 'ri-temp-cold-line' },
  { label: 'Heat Pumps', path: '/heat-pumps', icon: 'ri-recycle-line' },
  { label: 'Plumbing', path: '/plumbing', icon: 'ri-drop-line' },
  { label: 'Electrical', path: '/electrical', icon: 'ri-flashlight-line' },
  { label: 'Water Heaters', path: '/water-heaters', icon: 'ri-water-flash-line' },
  { label: 'Insulation', path: '/insulation', icon: 'ri-building-line' },
  { label: 'Air Quality', path: '/air-quality', icon: 'ri-windy-line' },
];

const plansMenu = [
  { label: 'Maintenance Plans', path: '/maintenance-plans', icon: 'ri-shield-check-line' },
  { label: 'Financing', path: '/financing', icon: 'ri-bank-card-line' },
  { label: 'Rebates & Incentives', path: '/rebates-incentives', icon: 'ri-coupon-3-line' },
];

const aboutMenu = [
  { label: 'About Renova', path: '/about', icon: 'ri-information-line' },
  { label: 'Commercial Services', path: '/commercial', icon: 'ri-building-2-line' },
  { label: 'Careers', path: '/careers', icon: 'ri-briefcase-line' },
  { label: 'Acquisition & Partnerships', path: '/about#acquisition', icon: 'ri-handshake-line' },
];

interface DropdownMenuProps {
  items: { label: string; path: string; icon: string }[];
  isOpen: boolean;
  onClose: () => void;
}

const DropdownMenu = ({ items, isOpen, onClose }: DropdownMenuProps) => {
  const navigate = useNavigate();
  if (!isOpen) return null;
  return (
    <div className="absolute top-full left-0 mt-1 bg-white border border-[#E2E8F0] rounded-xl min-w-[220px] z-50 overflow-hidden">
      {items.map((item) => (
        <button
          key={item.path}
          onClick={() => { navigate(item.path); onClose(); }}
          className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-[#374151] hover:bg-[#F8F9FB] hover:text-[#C41E3A] transition-colors cursor-pointer whitespace-nowrap text-left"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={`${item.icon} text-base text-[#C41E3A]`} />
          </div>
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenMenu(null); }, [location]);

  const toggle = (menu: string) => setOpenMenu(openMenu === menu ? null : menu);

  return (
    <nav ref={navRef} className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white border-b border-[#E2E8F0]' : 'bg-white border-b border-[#E2E8F0]'}`}>
      {/* Promo banner — sits above all nav content */}
      <PromoBanner />

      <div className="w-full px-4 lg:px-6">
        <div className="flex items-center" style={{ height: '88px' }}>

          {/* Logo — hard left, fixed width zone */}
          <div className="flex-shrink-0 flex items-center">
            <button onClick={() => navigate('/')} className="flex items-center cursor-pointer">
              <img
                src="https://storage.readdy-site.link/project_files/6746c619-ced5-4466-9905-8e7d3536934a/1f7edc7f-b5da-414b-bc5d-2dd0becc8078_renova-comfort-logo-no-bottom-text-1.png?v=44440c9b454f3025d02ad5b192de4240"
                alt="Renova Comfort"
                className="h-14 w-auto object-contain"
              />
            </button>
          </div>

          {/* Nav links — left-aligned right next to logo */}
          <div className="flex-1 hidden lg:flex items-center justify-start space-x-1 ml-6">
            {[
              { id: 'services', label: 'Services', items: servicesMenu },
              { id: 'plans', label: 'Plans & Financing', items: plansMenu },
            ].map(({ id, label, items }) => (
              <div key={id} className="relative">
                <button
                  onClick={() => toggle(id)}
                  className={`flex items-center space-x-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${openMenu === id ? 'text-[#C41E3A] bg-[#F8F9FB]' : 'text-[#374151] hover:text-[#C41E3A] hover:bg-[#F8F9FB]'}`}
                >
                  <span>{label}</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${openMenu === id ? 'rotate-180' : ''}`} />
                </button>
                <DropdownMenu items={items} isOpen={openMenu === id} onClose={() => setOpenMenu(null)} />
              </div>
            ))}

            <button onClick={() => navigate('/commercial')} className="px-4 py-2 rounded-lg text-sm font-medium text-[#374151] hover:text-[#C41E3A] hover:bg-[#F8F9FB] transition-colors cursor-pointer whitespace-nowrap">
              Commercial
            </button>

            <div className="relative">
              <button
                onClick={() => toggle('about')}
                className={`flex items-center space-x-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${openMenu === 'about' ? 'text-[#C41E3A] bg-[#F8F9FB]' : 'text-[#374151] hover:text-[#C41E3A] hover:bg-[#F8F9FB]'}`}
              >
                <span>About</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${openMenu === 'about' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={aboutMenu} isOpen={openMenu === 'about'} onClose={() => setOpenMenu(null)} />
            </div>

            <button onClick={() => navigate('/service-areas')} className="px-4 py-2 rounded-lg text-sm font-medium text-[#374151] hover:text-[#C41E3A] hover:bg-[#F8F9FB] transition-colors cursor-pointer whitespace-nowrap">
              Service Areas
            </button>
          </div>

          {/* CTA — hard right */}
          <div className="flex-shrink-0 hidden lg:flex items-center justify-end space-x-3">
            <a href="tel:+18506698335" className="flex items-center space-x-1.5 text-sm font-semibold text-[#0C2340] hover:text-[#C41E3A] transition-colors whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-[#C41E3A] text-sm" />
              </div>
              <span>(850) 669-8335</span>
            </a>
            <button
              onClick={() => navigate('/book-appointment')}
              className="bg-[#C41E3A] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-colors whitespace-nowrap cursor-pointer"
            >
              Book Now
            </button>
          </div>

          {/* Mobile hamburger */}
          <div className="lg:hidden ml-auto">
            <button className="p-2 cursor-pointer" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
              <i className={`text-2xl text-[#0C2340] ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
            </button>
          </div>

        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#E2E8F0] px-4 pb-6 max-h-[80vh] overflow-y-auto">
          {[
            { id: 'services', label: 'Services', items: servicesMenu },
            { id: 'plans', label: 'Plans & Financing', items: plansMenu },
            { id: 'about', label: 'About', items: aboutMenu },
          ].map(({ id, label, items }) => (
            <div key={id} className="border-b border-[#F3F4F6] py-3">
              <button
                onClick={() => setMobileSub(mobileSub === id ? null : id)}
                className="flex items-center justify-between w-full text-sm font-semibold text-[#0C2340] cursor-pointer"
              >
                <span>{label}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileSub === id ? 'rotate-180' : ''}`} />
              </button>
              {mobileSub === id && (
                <div className="mt-2 space-y-1 pl-3">
                  {items.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      className="flex items-center space-x-2 w-full text-sm text-[#6B7280] hover:text-[#C41E3A] py-1.5 cursor-pointer whitespace-nowrap text-left"
                    >
                      <i className={`${item.icon} text-[#C41E3A]`} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-4 space-y-3">
            <button onClick={() => { navigate('/commercial'); setMobileOpen(false); }} className="block w-full text-left text-sm font-semibold text-[#0C2340] py-2 cursor-pointer whitespace-nowrap">Commercial</button>
            <button onClick={() => { navigate('/service-areas'); setMobileOpen(false); }} className="block w-full text-left text-sm font-semibold text-[#0C2340] py-2 cursor-pointer whitespace-nowrap">Service Areas</button>
            <a href="tel:+18506698335" className="flex items-center space-x-2 text-sm font-semibold text-[#C41E3A]">
              <i className="ri-phone-fill" />
              <span>(850) 669-8335</span>
            </a>
            <button
              onClick={() => { navigate('/book-appointment'); setMobileOpen(false); }}
              className="w-full bg-[#C41E3A] text-white px-5 py-3 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              Book Appointment
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
